import { useEffect, useState } from 'react';

import { Box, Grow, Typography, Skeleton } from '@mui/material';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import dayjs from 'dayjs';
import { FormattedMessage } from 'react-intl';

import CallButtons from '@components/operatorSession/buttons/CallButtons';
import ReportButtons from '@components/operatorSession/buttons/ReportButtons';
import OperatorSessionNotes from '@components/operatorSession/OperatorSessionNotes';
import OperatorSessionReport from '@components/operatorSession/OperatorSessionReport';
import usePanelSelected from '@components/operatorSession/usePanelSelected';
import { useAccount } from '@contexts/AccountProvider';
import { useOperatorSession } from '@contexts/OperatorSeesionProvider';

const OperatorSessionFullPanel = () => {
    const { theme } = useTheme();
    const { sessionStartTime } = useOperatorSession();
    const { panelSelected, setPanelSelected } = usePanelSelected();
    const { account, isLoading } = useAccount();
    const [time, setTime] = useState(dayjs());

    useEffect(() => {
        const interval = setInterval(() => setTime(dayjs()), 1000);
        return () => {
            clearInterval(interval);
        };
    }, []);

    const name = account?.name;
    const isExtendedPanelOpen = panelSelected !== 'CLOSED';

    return (
        <Box display="flex">
            {panelSelected === 'NOTES' && (
                <OperatorSessionNotes
                    isOpen
                    close={() => setPanelSelected('CLOSED')}
                />
            )}

            {panelSelected === 'REPORT' && (
                <OperatorSessionReport
                    isOpen
                    close={() => setPanelSelected('CLOSED')}
                />
            )}

            {/*/TODO use theme.transitions.ease.easeIn may need to add something around this in the theme*/}
            <Grow
                in
                style={{ transformOrigin: 'right' }}
                easing={
                    theme.transitions?.ease?.easeIn ||
                    'cubic - bezier(0.0, 0, 0.2, 1)'
                }
                // easing="cubic-bezier(0.0, 0, 0.2, 1)"
                timeout={theme.transitions.duration.enteringScreen}>
                <Box
                    sx={{
                        zIndex: 10, // 1 higher than the notes panel
                        ...(isExtendedPanelOpen
                            ? {
                                  borderBottomLeftRadius:
                                      theme.shape.borderRadius,
                                  borderBottomRightRadius:
                                      theme.shape.borderRadius,
                              }
                            : {
                                  borderRadius: 1,
                              }),
                        boxShadow: 3,
                        width: theme.spacing(56.25),
                        height: theme.spacing(9.5),
                        position: 'fixed',
                        bottom: theme.spacing(3),
                        backgroundColor: 'primary.main',
                        border: '0',
                        ml: 'auto',
                        px: 2,
                        right: theme.spacing(3.25),
                    }}>
                    <Box
                        sx={{
                            mt: 0.75,
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                mt: 0.75,
                            }}>
                            {isLoading ? (
                                <Skeleton
                                    data-testid="name-loader"
                                    width={theme.spacing(10)}
                                    sx={{
                                        backgroundColor: 'primary.contrastText',
                                    }}
                                />
                            ) : (
                                <Typography
                                    variant="subtitle2"
                                    color="primary.contrastText">
                                    {name}
                                </Typography>
                            )}
                            <Typography
                                color="primary.contrastText"
                                data-testid="timer"
                                fontWeight="600">
                                <FormattedMessage
                                    id="OperatorSessionFullPanel.session"
                                    defaultMessage="Session {time}"
                                    values={{
                                        time: `${dayjs
                                            .duration(
                                                time?.diff(sessionStartTime),
                                            )
                                            .format('mm:ss')}`,
                                    }}
                                />
                            </Typography>
                        </Box>
                        <Box display="flex" marginLeft="auto">
                            {panelSelected !== 'REPORT' ? (
                                <CallButtons
                                    onButtonClick={setPanelSelected}
                                    openPanel={panelSelected}
                                />
                            ) : (
                                <ReportButtons onCancel={setPanelSelected} />
                            )}
                        </Box>
                    </Box>
                </Box>
            </Grow>
        </Box>
    );
};

export default OperatorSessionFullPanel;
